// Envio de uma oferta do feed para grupos/canais: publica agora pela fila de
// posts (create_scheduled_post), o mesmo caminho do Criar Posts. PRO.

import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useEntitlements } from "@/lib/entitlements";
import { useGroups } from "@/lib/hooks/useGroups";
import { useCreateScheduledPost, type CreateScheduledPostDeliveryInput } from "@/lib/hooks/useScheduledPosts";
import { useTenant } from "@/lib/hooks/useTenant";
import { useWhatsappChats } from "@/lib/hooks/useWhatsapp";
import type { BroadcastLinkData } from "@lucralink/shared";
import { BroadcastTextPicker } from "@/components/posts/BroadcastTextPicker";

interface SendOfferFormProps {
  readonly text: string;
  readonly imageUrl: string | null;
  readonly linkId: string | null;
  /** Dados da oferta para os textos de disparo (opcional). */
  readonly link?: BroadcastLinkData | null;
}

const MAX_DESTINATIONS = 10;

function useDestinationOptions(): CreateScheduledPostDeliveryInput[] {
  const { data: groups = [] } = useGroups();
  const { data: chats = [] } = useWhatsappChats();
  return useMemo(
    () => [
      ...groups
        .filter((g) => g.status === "active")
        .map((g) => ({ channel: "telegram" as const, targetId: String(g.telegram_chat_id), label: g.title ?? `Grupo ${g.telegram_chat_id}` })),
      ...chats
        .filter((c) => c.status === "active" && c.role === "destino")
        .map((c) => ({ channel: "whatsapp" as const, targetId: c.jid, label: c.title ?? c.jid })),
    ],
    [groups, chats],
  );
}

export function SendOfferForm({ text, imageUrl, linkId, link = null }: SendOfferFormProps): JSX.Element {
  const { data: tenantBilling } = useTenant();
  const entitlements = useEntitlements(tenantBilling?.tenant);
  const options = useDestinationOptions();
  const create = useCreateScheduledPost();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [chosenText, setChosenText] = useState<string | null>(null);

  if (!entitlements.hasScheduledPosts) {
    return (
      <p className="text-xs text-muted-foreground">
        Enviar direto para os grupos é do plano PRO.{" "}
        <Link to="/app/plans" className="text-primary hover:underline">
          Ver planos
        </Link>
      </p>
    );
  }
  if (options.length === 0) {
    return <p className="text-xs text-muted-foreground">Nenhum grupo ou canal de destino conectado ainda.</p>;
  }

  const key = (o: CreateScheduledPostDeliveryInput): string => `${o.channel}:${o.targetId}`;
  const toggle = (o: CreateScheduledPostDeliveryInput): void => {
    const next = new Set(selected);
    if (next.has(key(o))) next.delete(key(o));
    else if (next.size < MAX_DESTINATIONS) next.add(key(o));
    setSelected(next);
  };
  const send = async (): Promise<void> => {
    try {
      await create.mutateAsync({
        bodyText: chosenText ?? text,
        // WhatsApp e texto-only no executor de posts: imagem so quando nao ha WhatsApp.
        imageUrl: options.some((o) => selected.has(key(o)) && o.channel === "whatsapp") ? null : imageUrl,
        linkId,
        scheduledAt: null,
        deliveries: options.filter((o) => selected.has(key(o))),
      });
      setResult({ ok: true, text: "Na fila de envio. Sai em até 30 segundos." });
      setSelected(new Set());
    } catch (error) {
      setResult({ ok: false, text: error instanceof Error ? error.message : "Não foi possível enviar." });
    }
  };

  return (
    <div className="space-y-2">
      <BroadcastTextPicker link={link} onInsert={setChosenText} />
      {chosenText && (
        <p className="line-clamp-3 whitespace-pre-line rounded border border-border p-2 text-[11px] text-muted-foreground">{chosenText}</p>
      )}
      <ul className="max-h-40 space-y-1 overflow-y-auto">
        {options.map((o) => (
          <li key={key(o)}>
            <label className="flex items-center gap-2 text-xs text-foreground">
              <input type="checkbox" className="h-3.5 w-3.5 accent-[hsl(var(--primary))]" checked={selected.has(key(o))} onChange={() => toggle(o)} />
              <span className="truncate">{o.label}</span>
              <span className="text-muted-foreground">{o.channel === "whatsapp" ? "WhatsApp" : "Telegram"}</span>
            </label>
          </li>
        ))}
      </ul>
      <Button size="sm" onClick={() => void send()} disabled={selected.size === 0 || create.isPending}>
        {create.isPending ? "Enviando..." : `Enviar agora (${selected.size})`}
      </Button>
      {result && (
        <p role={result.ok ? "status" : "alert"} className={result.ok ? "text-xs text-primary" : "text-xs text-destructive"}>
          {result.text}
        </p>
      )}
    </div>
  );
}
